/**
 * Typed calls to `/v1/txplan` (contracts `@velora/contracts/txplan`) and
 * the query keys. Keys start with `tx_case`, so live updates for that
 * entity refetch every treatment-plan screen (useLive).
 */
import {
  AcceptanceReportSchema,
  ApprovalFeedSchema,
  PredeterminationListSchema,
  ReadyToPresentSchema,
  TxEligibilityRunSchema,
  type CreatePredetermination,
  type UpdatePredetermination,
  CreatedLinkSchema,
  ExplainSchema,
  FunnelSchema,
  PatientPlansSchema,
  SlotsSchema,
  TxBoardSchema,
  WorklistSchema,
  type Accept,
  type AcceptanceReportQuery,
  type AddItems,
  type CreateCase,
  type DuplicateOption,
  type FollowupOutcome,
  type MoveItems,
  type SignatureInk,
  type TxSettings,
  TxSettingsViewSchema,
  type UpdatePhase,
  type WorklistFilter,
} from '@velora/contracts/txplan';
import {
  PresentationSendResultSchema,
  PresentationStateSchema,
  VerifyBenefitsResultSchema,
  type CreatePresentation,
  type PresentationSend,
  type UpdatePresentation,
} from '@velora/contracts/txplan-presentation';
import { OFFICE_QUERY_PARAM } from '@velora/contracts/core';
import { ApiError, buildPath, type HttpClient } from '@velora/sdk';
import { z } from 'zod';

export const TX = 'tx_case';

/**
 * TP-18: the planner and the patient's plans also refetch on chart procedure
 * events, so a TP filling added in the chart shows in Unassigned without a
 * reload.
 */
const PROC_LIVE = { live: ['procedure'] } as const;

export const keys = {
  worklist: (filter: WorklistFilter, q: string, offset = 0, minBenefitsCents?: number) =>
    minBenefitsCents === undefined
      ? ([TX, 'worklist', filter, q, offset] as const)
      : ([TX, 'worklist', filter, q, offset, { minBenefitsCents }] as const),
  funnel: (days: number) => [TX, 'funnel', days] as const,
  approvalFeed: () => [TX, 'approval-feed'] as const,
  readyToPresent: (limit: number) => [TX, 'ready', limit] as const,
  predeterminations: (status: 'open' | 'all') => [TX, 'predeterminations', status] as const,
  acceptance: (q: AcceptanceReportQuery) => [TX, 'acceptance', q] as const,
  board: (caseId: string) => [TX, 'board', caseId, PROC_LIVE] as const,
  patient: (patientId: string) => [TX, 'patient', patientId, PROC_LIVE] as const,
  /**
   * The patient's plans in an office scope. Every screen that reads them
   * uses this key, so the New plan drawer and the planner share one request
   * (MTX-TX-26).
   */
  patientIn: (patientId: string, office: string) =>
    [TX, 'patient', patientId, { office, ...PROC_LIVE }] as const,
  slots: (caseId: string) => [TX, 'slots', caseId] as const,
  settings: (office: string) => [TX, 'settings', office] as const,
  presentation: (caseId: string) => [TX, 'presentation', caseId] as const,
};

/**
 * URLs the browser opens directly (the review iframe, the PDF): cookies carry
 * the session and the office travels as a query parameter.
 */
export const presentationUrls = {
  html: (docId: string, mode: 'review' | 'web' | 'present', office: string) =>
    `/v1/txplan/presentation/${encodeURIComponent(docId)}/html?mode=${mode}&${OFFICE_QUERY_PARAM}=${encodeURIComponent(office)}`,
  pdf: (docId: string, purpose: 'download' | 'print', office: string) =>
    `/v1/txplan/presentation/${encodeURIComponent(docId)}/pdf?purpose=${purpose}&${OFFICE_QUERY_PARAM}=${encodeURIComponent(office)}`,
  /** The one-page chairside summary: `review` preview, or `print` (approved; opens the print dialog). */
  summary: (docId: string, mode: 'review' | 'print', office: string) =>
    `/v1/txplan/presentation/${encodeURIComponent(docId)}/summary?mode=${mode}&${OFFICE_QUERY_PARAM}=${encodeURIComponent(office)}`,
  summaryPdf: (docId: string, office: string) =>
    `/v1/txplan/presentation/${encodeURIComponent(docId)}/summary/pdf?${OFFICE_QUERY_PARAM}=${encodeURIComponent(office)}`,
};

const base = '/txplan';

export const txApi = {
  presentation: (c: HttpClient, caseId: string, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/cases/:caseId/presentation`, { caseId }), {
      schema: PresentationStateSchema,
      ...(signal ? { signal } : {}),
    }),
  startPresentation: (c: HttpClient, caseId: string, body: CreatePresentation) =>
    c.request(buildPath(`${base}/cases/:caseId/presentation`, { caseId }), {
      method: 'POST',
      body,
      schema: PresentationStateSchema,
    }),
  editPresentation: (c: HttpClient, docId: string, body: UpdatePresentation) =>
    c.request(buildPath(`${base}/presentation/:docId`, { docId }), {
      method: 'PATCH',
      body,
      schema: PresentationStateSchema,
    }),
  generatePresentation: (c: HttpClient, docId: string) =>
    c.request(buildPath(`${base}/presentation/:docId/generate`, { docId }), {
      method: 'POST',
      schema: PresentationStateSchema,
    }),
  /** Owner item 7: a front-desk task to verify coverage the plan template does not know. */
  verifyBenefits: (c: HttpClient, docId: string) =>
    c.request(buildPath(`${base}/presentation/:docId/verify-benefits`, { docId }), {
      method: 'POST',
      schema: VerifyBenefitsResultSchema,
    }),
  /** TC role test fix 3: ask the plan's dentist to approve the draft. */
  requestApproval: (c: HttpClient, docId: string, note?: string) =>
    c.request(buildPath(`${base}/presentation/:docId/request-approval`, { docId }), {
      method: 'POST',
      body: note ? { note } : {},
      schema: PresentationStateSchema,
    }),
  approvalFeed: (c: HttpClient, signal?: AbortSignal) =>
    c.request(`${base}/approval-requests`, {
      schema: ApprovalFeedSchema,
      ...(signal ? { signal } : {}),
    }),
  readyToPresent: (c: HttpClient, limit = 8, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/ready-to-present`, undefined, { limit }), {
      schema: ReadyToPresentSchema,
      ...(signal ? { signal } : {}),
    }),
  predeterminations: (c: HttpClient, status: 'open' | 'all', signal?: AbortSignal) =>
    c.request(buildPath(`${base}/predeterminations`, undefined, { status }), {
      schema: PredeterminationListSchema,
      ...(signal ? { signal } : {}),
    }),
  /** TC role test fix 7: run eligibility for this patient from the plan. */
  runEligibility: (c: HttpClient, caseId: string) =>
    c.request(buildPath(`${base}/cases/:caseId/eligibility`, { caseId }), {
      method: 'POST',
      body: {},
      schema: TxEligibilityRunSchema,
    }),
  recordPredetermination: (c: HttpClient, caseId: string, body: CreatePredetermination) =>
    c.request(buildPath(`${base}/cases/:caseId/predeterminations`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  updatePredetermination: (c: HttpClient, id: string, body: UpdatePredetermination) =>
    c.request(buildPath(`${base}/predeterminations/:id`, { id }), {
      method: 'PATCH',
      body,
      schema: TxBoardSchema,
    }),
  approvePresentation: (c: HttpClient, docId: string) =>
    c.request(buildPath(`${base}/presentation/:docId/approve`, { docId }), {
      method: 'POST',
      schema: PresentationStateSchema,
    }),
  sendPresentation: (c: HttpClient, docId: string, body: PresentationSend) =>
    c.request(buildPath(`${base}/presentation/:docId/send`, { docId }), {
      method: 'POST',
      body,
      schema: PresentationSendResultSchema,
    }),
  worklist: (
    c: HttpClient,
    filter: WorklistFilter,
    q: string,
    signal?: AbortSignal,
    offset = 0,
    /** Benefits remaining filter: at least this much annual maximum left (cents). */
    minBenefitsCents?: number,
  ) =>
    c.request(
      buildPath(`${base}/worklist`, undefined, {
        filter,
        q: q || undefined,
        offset: offset > 0 ? offset : undefined,
        minBenefitsCents,
      }),
      {
        schema: WorklistSchema,
        ...(signal ? { signal } : {}),
      },
    ),
  funnel: (c: HttpClient, days: number, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/funnel`, undefined, { days }), {
      schema: FunnelSchema,
      ...(signal ? { signal } : {}),
    }),
  acceptance: (c: HttpClient, q: AcceptanceReportQuery, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/acceptance`, undefined, { ...q }), {
      schema: AcceptanceReportSchema,
      ...(signal ? { signal } : {}),
    }),
  board: (c: HttpClient, caseId: string, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/cases/:caseId`, { caseId }), {
      schema: TxBoardSchema,
      ...(signal ? { signal } : {}),
    }),
  /** The patient presenter's board: the patient's own estimate even without money.view. */
  presentBoard: (c: HttpClient, caseId: string, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/cases/:caseId`, { caseId }, { present: '1' }), {
      schema: TxBoardSchema,
      ...(signal ? { signal } : {}),
    }),
  patient: (c: HttpClient, patientId: string, signal?: AbortSignal) =>
    c.request(buildPath(`${base}/patients/:patientId`, { patientId }), {
      schema: PatientPlansSchema,
      ...(signal ? { signal } : {}),
    }),
  explain: (c: HttpClient, caseId: string, optionId: string, itemId: string) =>
    c.request(
      buildPath(`${base}/cases/:caseId/options/:optionId/items/:itemId/explain`, {
        caseId,
        optionId,
        itemId,
      }),
      { schema: ExplainSchema },
    ),
  slots: (c: HttpClient, caseId: string, visitId?: string) =>
    c.request(buildPath(`${base}/cases/:caseId/slots`, { caseId }, { visitId }), {
      schema: SlotsSchema,
    }),
  signature: (c: HttpClient, caseId: string, signatureId: string) =>
    c.request(buildPath(`${base}/cases/:caseId/signatures/:signatureId`, { caseId, signatureId }), {
      schema: z.object({
        id: z.string(),
        version: z.number(),
        kind: z.string(),
        signerName: z.string(),
        channel: z.string(),
        signedAt: z.string(),
        ip: z.string().nullable(),
        documentSha256: z.string(),
        hashVerified: z.boolean(),
        document: z.unknown(),
        ink: z.unknown().nullable(),
      }),
    }),

  create: (c: HttpClient, body: CreateCase) =>
    c.request(`${base}/cases`, { method: 'POST', body, schema: TxBoardSchema }),
  updateCase: (
    c: HttpClient,
    caseId: string,
    body: { title?: string; expiresOn?: string | null },
  ) =>
    c.request(buildPath(`${base}/cases/:caseId`, { caseId }), {
      method: 'PATCH',
      body,
      schema: TxBoardSchema,
    }),
  duplicate: (c: HttpClient, optionId: string, body: DuplicateOption) =>
    c.request(buildPath(`${base}/options/:optionId/duplicate`, { optionId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  recommend: (c: HttpClient, optionId: string) =>
    c.request(buildPath(`${base}/options/:optionId/recommend`, { optionId }), {
      method: 'POST',
      schema: TxBoardSchema,
    }),
  renameOption: (c: HttpClient, optionId: string, body: { name: string }) =>
    c.request(buildPath(`${base}/options/:optionId`, { optionId }), {
      method: 'PATCH',
      body,
      schema: TxBoardSchema,
    }),
  removeOption: (c: HttpClient, optionId: string) =>
    c.request(buildPath(`${base}/options/:optionId`, { optionId }), {
      method: 'DELETE',
      schema: TxBoardSchema,
    }),
  updatePhase: (c: HttpClient, phaseId: string, body: UpdatePhase) =>
    c.request(buildPath(`${base}/phases/:phaseId`, { phaseId }), {
      method: 'PATCH',
      body,
      schema: TxBoardSchema,
    }),
  addItems: (c: HttpClient, optionId: string, body: AddItems) =>
    c.request(buildPath(`${base}/options/:optionId/items`, { optionId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  move: (c: HttpClient, body: MoveItems) =>
    c.request(`${base}/items/move`, { method: 'POST', body, schema: TxBoardSchema }),
  /** Removes a line from its option (the procedure stays planned on the chart). */
  removeItem: (c: HttpClient, itemId: string) =>
    c.request(buildPath(`${base}/items/:itemId/remove`, { itemId }), {
      method: 'POST',
      body: {},
      schema: TxBoardSchema,
    }),
  createPhase: (
    c: HttpClient,
    optionId: string,
    body: { name: string; targetDate?: string | null },
  ) =>
    c.request(buildPath(`${base}/options/:optionId/phases`, { optionId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  exclude: (c: HttpClient, itemId: string, excluded: boolean) =>
    c.request(buildPath(`${base}/items/:itemId/exclude`, { itemId }), {
      method: 'POST',
      body: { excluded },
      schema: TxBoardSchema,
    }),
  reorder: (c: HttpClient, visitId: string, itemIds: string[]) =>
    c.request(buildPath(`${base}/visits/:visitId/order`, { visitId }), {
      method: 'POST',
      body: { itemIds },
      schema: TxBoardSchema,
    }),
  present: (c: HttpClient, caseId: string) =>
    c.request(buildPath(`${base}/cases/:caseId/present`, { caseId }), {
      method: 'POST',
      schema: TxBoardSchema,
    }),
  link: (c: HttpClient, caseId: string, body: { channel: 'sms' | 'copy'; message?: string }) =>
    c.request(buildPath(`${base}/cases/:caseId/links`, { caseId }), {
      method: 'POST',
      body,
      schema: CreatedLinkSchema,
    }),
  revokeLink: (c: HttpClient, linkId: string) =>
    c.request(buildPath(`${base}/links/:linkId`, { linkId }), {
      method: 'DELETE',
      schema: TxBoardSchema,
    }),
  accept: (c: HttpClient, caseId: string, body: Accept) =>
    c.request(buildPath(`${base}/cases/:caseId/accept`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  decline: (
    c: HttpClient,
    caseId: string,
    body: {
      reason: Accept['declineReason'];
      note?: string;
      callback: boolean;
      /** TC role test fix 12: callback task due date (YYYY-MM-DD). */
      callbackOn?: string;
      /** TC role test fix 12: declined by phone, staff attests. */
      verbalAttestation?: string;
      /** TP-11: the patient's signed refusal (required when the office's refusal consent is on). */
      refusal?: {
        signerName: string;
        signerRole: 'patient' | 'guardian';
        signature: SignatureInk;
        acknowledged: true;
      };
    },
  ) =>
    c.request(buildPath(`${base}/cases/:caseId/decline`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  /** Referred out (Dentrix "Referred"). */
  refer: (c: HttpClient, caseId: string, body: { referredTo: string; note?: string }) =>
    c.request(buildPath(`${base}/cases/:caseId/refer`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  reopen: (c: HttpClient, caseId: string) =>
    c.request(buildPath(`${base}/cases/:caseId/reopen`, { caseId }), {
      method: 'POST',
      schema: TxBoardSchema,
    }),
  revise: (c: HttpClient, caseId: string) =>
    c.request(buildPath(`${base}/cases/:caseId/revise`, { caseId }), {
      method: 'POST',
      schema: TxBoardSchema,
    }),
  financing: (c: HttpClient, caseId: string, body: { quoteId: string; downCents: number }) =>
    c.request(buildPath(`${base}/cases/:caseId/financing`, { caseId }), {
      method: 'PUT',
      body,
      schema: TxBoardSchema,
    }),
  followup: (c: HttpClient, caseId: string, body: { outcome: FollowupOutcome; note?: string }) =>
    c.request(buildPath(`${base}/cases/:caseId/followups`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
  settings: (c: HttpClient, signal?: AbortSignal) =>
    c.request(`${base}/settings`, {
      schema: TxSettingsViewSchema,
      ...(signal ? { signal } : {}),
    }),
  saveSettings: (c: HttpClient, body: Partial<TxSettings>) =>
    c.request(`${base}/settings`, { method: 'PUT', body, schema: TxSettingsViewSchema }),
  book: (
    c: HttpClient,
    caseId: string,
    body: {
      visitId: string;
      startsAt: string;
      providerId: string;
      operatoryId: string | null;
      lengthMin: number;
    },
  ) =>
    c.request(buildPath(`${base}/cases/:caseId/book`, { caseId }), {
      method: 'POST',
      body,
      schema: TxBoardSchema,
    }),
};

/** Message to show for a failed call: the server's sentence when it sent one. */
export function problemText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
    if (err.status === 403) return 'You do not have permission to do that.';
    if (err.status === 409)
      return 'That step is not available any more. The plan may have moved on.';
  }
  return 'That did not work. Check the connection and try again.';
}
